import React from 'react';
import styled from 'styled-components';
import { useGame } from '../contexts/GameContext';

const ScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px auto;
  background: rgba(30, 30, 40, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 25px;
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ScoreValue = styled.div`
  font-size: 56px;
  font-weight: 800;
  background: linear-gradient(90deg, #9945FF, #14F195);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 15px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  display: inline-block;
  
  &::after {
    content: 'POINTS';
    position: absolute;
    top: -5px;
    right: -48px;
    font-size: 11px;
    background: linear-gradient(45deg, #9945FF, #14F195);
    color: white;
    padding: 4px 10px;
    border-radius: 10px;
    transform: rotate(15deg);
    font-weight: 700;
    -webkit-text-fill-color: white;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
`;

const ScoreLabel = styled.div`
  font-size: 20px;
  color: white;
  font-weight: 600;
  margin-bottom: 5px;
  position: relative;
  padding-bottom: 10px;
`;

const ScoreProgressContainer = styled.div`
  width: 100%;
  max-width: 500px;
  height: 16px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin: 20px 0;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ScoreProgressBar = styled.div<{ percent: number }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: ${props => {
    if (props.percent < 30) return 'linear-gradient(90deg, #FF5F6D, #FF9B5F)';
    if (props.percent < 70) return 'linear-gradient(90deg, #FFD166, #F6FA70)';
    return 'linear-gradient(90deg, #00F5A0, #00D9F5)';
  }};
  transition: width 1s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  
  &::after {
    content: '${props => Math.round(props.percent)}%';
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: white;
    font-size: 10px;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    display: ${props => props.percent < 15 ? 'none' : 'block'};
  }
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 500px;
  margin-bottom: 5px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const ProgressStart = styled.span``;
const ProgressEnd = styled.span``;

const AnsweredQuestions = styled.div`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);
  padding: 8px 16px;
  border-radius: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  svg {
    margin-right: 8px;
    width: 20px;
    height: 20px;
    fill: #14F195;
  }
`;

const ShareButton = styled.button`
  background: linear-gradient(45deg, #1da1f2, #0d8bd9);
  color: white;
  font-size: 15px;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 24px;
  display: flex;
  align-items: center;
  transition: all 0.3s;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 6px 15px rgba(29, 161, 242, 0.3);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(29, 161, 242, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    margin-right: 8px;
    width: 18px;
    height: 18px;
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const TargetIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M12,10.5A1.5,1.5 0 0,0 10.5,12A1.5,1.5 0 0,0 12,13.5A1.5,1.5 0 0,0 13.5,12A1.5,1.5 0 0,0 12,10.5Z" />
  </svg>
);

const ScoreDisplay: React.FC = () => {
  const { creditScore, answeredQuestions } = useGame();
  
  // Calculate percentage (max score is 1000)
  const scorePercent = Math.min((creditScore / 1000) * 100, 100);
  
  const handleShare = () => {
    const shareText = `I just got a ${creditScore} tariff score on @tariffscore!`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank');
  };

  return (
    <ScoreContainer>
      <ScoreLabel>Tariff Score</ScoreLabel>
      <ScoreValue>{creditScore}</ScoreValue>
      
      <ProgressInfo>
        <ProgressStart>0</ProgressStart>
        <ProgressEnd>1000</ProgressEnd>
      </ProgressInfo>
      
      <ScoreProgressContainer>
        <ScoreProgressBar percent={scorePercent} />
      </ScoreProgressContainer>
      
      <AnsweredQuestions>
        <TargetIcon />
        Decisions made: {answeredQuestions}
      </AnsweredQuestions>
      <ShareButton onClick={handleShare}>
        Share on X
      </ShareButton>
    </ScoreContainer>
  );
};

export default ScoreDisplay;